import cn from 'classnames';
import AmountInput from 'components/amount-input-new/input';
import ShippingElement from 'components/shipping-element';
import { useConfig } from 'config';
import { FeatureFlag, getFeatureFlag } from 'constants/feature-flag';
import { OrderType } from 'constants/order';
import { useAuth } from 'contexts/auth';
import { useCommon } from 'contexts/common';
import useExperiment from 'hooks/common/use-experiment';
import { useIsExistingCustomer } from 'hooks/common/use-is-existing-customer';
import useHasSubscription from 'hooks/subscriptions/use-has-subscription';
import { CrossSellProductDetails } from 'interfaces/product';
import { SubscriptionDetails } from 'interfaces/subscription';
import useTestPT6874 from 'modules/cart/components/tiny-cart/hooks/use-test-pt6874';
import { useContentful } from 'modules/content-cms/components/context';
import { useProductDetailsHeaderState } from 'modules/product-details/components/product-details-header/state';
import useProductDetailsCart from 'modules/product-details/hooks/useProductDetailsCart';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useProductDetailsStore } from 'stores/product-details';
import { isSubscriptionEnabled } from 'utils/product';
import { OneOffItemInput } from '../add-to-exisiting-subscription-button/use-add-to-existing-subscription';
import BuyButton from './components/buy-button';
import OrderTypeForm from './components/order-type-form';
import PriceElement from './components/price-element';
import SubscriptionIntervalForm from './components/subscription-interval-form';
import VariantSelector from './components/variant-selector';
import useDefaultSubscriptionFoodType from './hooks/use-default-subscruption-food-types';

const AddToExistingSubscriptionButton = dynamic(
  () => import('../add-to-exisiting-subscription-button'),
  {
    ssr: false,
  }
);

const SubscriptionConfirmationInformation = dynamic(
  () => import('../subscription-confirmation-information'),
  {
    ssr: false,
  }
);
const CouponDisplay = dynamic(() => import('./components/coupon-display'), {
  ssr: false,
});

const BackInStockModal = dynamic(
  () => import('./components/back-in-stock-modal'),
  {
    ssr: false,
  }
);
const CrossSellPDP = dynamic(() => import('./components/cross-sell'));
const RecommendedAlternative = dynamic(
  () => import('./components/recommended-alternative')
);

const EtaCommunication = dynamic(
  () => import('./components/eta-communication'),
  {
    ssr: false,
  }
);

const VariantSelectorTest = dynamic(
  () => import('./components/variant-selector-test'),
  {
    ssr: false,
  }
);

const TinyCart = dynamic(() => import('modules/cart/components/tiny-cart'), {
  ssr: false,
});

const BuyPanel: React.FC = () => {
  const { shopId } = useConfig();

  const [isCartAnimationOn, setIsCartAnimationOn] = useState(false);
  const [backInStockModalVisible, setBackInStockModalVisible] = useState(false);
  const { asPath } = useRouter();
  const { onAddToCart } = useProductDetailsCart();
  const {
    couponPrices,
    crossSellProductDetails,
    crossSellProductQuantity,
    oneOffPrice,
    orderType,
    price,
    pricePerKilo,
    product,
    selectedVariant,
    selectedVariantId,
    subscriptionInterval,
    variantQuantity,
    onOrderTypeChange,
    onSubscriptionIntervalChange,
    onVariantIdChange,
    onVariantQuantityChange,
  } = useProductDetailsHeaderState();

  const { isEc } = useIsExistingCustomer();

  // Check if the product is a test product and exclude it from the add to existing subscription logic
  const isTestOosItem = product?.tags.includes('test_oos');

  const crossSellPdpFlg = getFeatureFlag(FeatureFlag.crossSellPdp, shopId);

  const [subscriptionEnabled, setIsSubscriptionEnabled] = useState(false);

  // Check if the product is subscription enabled by checking if the coupon prices have a subscription variant
  useEffect(() => {
    if (couponPrices?.variants.some((variant) => variant.subscription)) {
      setIsSubscriptionEnabled(
        isSubscriptionEnabled(product.tags, Number(product.id))
      );
    } else {
      setIsSubscriptionEnabled(false);
    }
  }, [couponPrices?.variants, product]);

  const { customer } = useAuth();
  const isPreorderProduct = product && product.tags.includes('Preorder');
  const isVariantAvailable = selectedVariant?.availableForSale;
  const { data: contentful } = useContentful();

  const {
    currentOrderType,
    hasUserChangedOrderType,
    setCurrentOrderType,
    setHasUserChangedOrderType,
  } = useProductDetailsStore();

  const { isSubDefault } = useDefaultSubscriptionFoodType({
    product,
  });

  useEffect(() => {
    if (!hasUserChangedOrderType) {
      onOrderTypeChange(
        isSubDefault ? OrderType.SUBSCRIPTION : OrderType.ONE_OFF
      );
    } else if (hasUserChangedOrderType) {
      onOrderTypeChange(currentOrderType);
    }
  }, [
    currentOrderType,
    hasUserChangedOrderType,
    isSubDefault,
    onOrderTypeChange,
  ]);

  const changeOrderType = useCallback(
    (orderType: OrderType) => {
      !hasUserChangedOrderType && setHasUserChangedOrderType(true);
      onOrderTypeChange(orderType);
      setCurrentOrderType(orderType);
    },
    [
      hasUserChangedOrderType,
      onOrderTypeChange,
      setCurrentOrderType,
      setHasUserChangedOrderType,
    ]
  );

  /**
   * We set the state to false when moving to another page and the product is not subscriable.
   * This prevents the issue of passing directly from a PDP of a product with a subscription to a PDP of a product without a subscription tag, that the interval menu remains visible.
   */
  useEffect(() => {
    if (!subscriptionEnabled) setHasUserChangedOrderType(false);
  }, [asPath, setHasUserChangedOrderType, subscriptionEnabled]);

  const [showTinyCart, setShowTinyCart] = useState(false);
  const { variant: variantTestPT6874 } = useTestPT6874();

  const _onAddToCart: React.MouseEventHandler<HTMLButtonElement> =
    useCallback(async () => {
      await onAddToCart();
      if (variantTestPT6874 !== 0) {
        setShowTinyCart(true);
      }
      setIsCartAnimationOn(true);
      const timeoutId = setTimeout(() => setIsCartAnimationOn(false), 6000);
      return () => clearTimeout(timeoutId);
    }, [onAddToCart, variantTestPT6874]);

  const { hasSubscription } = useHasSubscription();

  //PT-7733: FE: Re-open A/B Test: Savings for tray price display PDP (PT-6298)
  // @see https://petsdeli.atlassian.net/browse/PT-7733
  const { loading: loadingTest773, variant: variantTest773 } = useExperiment({
    baseEntryId: 'SAUE0gAVzUEE7I6uq0AcX',
  });

  const oneOffItemsInput = useMemo(() => {
    const input: Array<OneOffItemInput | CrossSellProductDetails> = [];
    if (crossSellProductDetails && crossSellProductQuantity > 0) {
      input.push(crossSellProductDetails);
    }
    if (orderType === 'one-off' && selectedVariant) {
      input.push({
        product: product,
        quantity: variantQuantity,
        productId: Number(product.id),
        variantId: Number(selectedVariant.id),
      });
    }
    return input.length > 0 ? input : undefined;
  }, [
    crossSellProductDetails,
    crossSellProductQuantity,
    orderType,
    selectedVariant,
    product,
    variantQuantity,
  ]);

  const [subResult, setSubResult] = useState<SubscriptionDetails | null>(null);

  const callback = useCallback(
    (arg?: PromiseSettledResult<SubscriptionDetails>[]) => {
      setSubResult(null);
      if (arg && arg[arg.length - 1].status === 'fulfilled') {
        const result = arg[
          arg.length - 1
        ] as PromiseFulfilledResult<SubscriptionDetails>;
        setSubResult(result.value);
      }
    },
    []
  );
  const selectedVariantDiscount = useMemo(() => {
    return couponPrices?.variants?.find(
      (v) => v.id === selectedVariant?.id + ''
    );
  }, [couponPrices?.variants, selectedVariant?.id]);

  const { globalDiscount } = useCommon();
  const { subscriptionDiscount: defaultSubscriptionDiscount } = useConfig();

  const subscriptionDiscount =
    couponPrices?.variants?.find((cp) => cp.id === selectedVariantId + '')
      ?.subscription?.discounts[0].value ||
    globalDiscount.discount?.firstSubscriptionOrderDiscount ||
    defaultSubscriptionDiscount;

  return (
    <>
      {showTinyCart && (
        <TinyCart
          onCloseCallback={() => {
            setShowTinyCart(false);
          }}
        />
      )}
      <form>
        <div className="border-grey-200 rounded-lg border py-3">
          <div
            className={cn(
              'px-4 lg:gap-4',
              variantTest773 === 1
                ? 'flex flex-col gap-4 lg:flex-row'
                : 'grid grid-cols-6 gap-6'
            )}
          >
            <div
              className={cn(
                'col-span-6',
                variantTest773 === 1
                  ? 'lg:col-span-4 lg:flex-[2]'
                  : 'lg:col-span-4'
              )}
            >
              {(!loadingTest773 || variantTest773 !== undefined) &&
                (variantTest773 === 1 && selectedVariantDiscount ? (
                  <VariantSelectorTest
                    couponPrices={couponPrices}
                    orderType={orderType}
                    pricePerKilo={pricePerKilo}
                    product={product}
                    selectedVariant={selectedVariant}
                    selectedVariantDiscount={selectedVariantDiscount}
                    onVariantIdChange={onVariantIdChange}
                  />
                ) : variantTest773 === 0 ? (
                  <VariantSelector
                    product={product}
                    selectedVariantId={selectedVariantId}
                    onVariantIdChange={onVariantIdChange}
                  />
                ) : (
                  <div className="bg-grey-600 h-10 animate-pulse rounded-lg opacity-40" />
                ))}
            </div>

            {selectedVariant?.availableForSale && (
              <div
                className={cn(
                  'col-span-6',
                  variantTest773 === 1
                    ? 'lg:col-span-2 lg:flex lg:flex-[1] lg:flex-col'
                    : 'lg:col-span-2'
                )}
              >
                <label
                  htmlFor="amount-input"
                  className="text-text-secondary mb-1 inline-block text-sm"
                >
                  <FormattedMessage id="pdp:buy-panel:amount" />
                </label>

                <AmountInput
                  data-test="amount-input"
                  minValue={1}
                  value={variantQuantity}
                  onChange={onVariantQuantityChange}
                  className={cn(
                    'min-h-[42px]',
                    variantTest773 === 1 ? 'flex-1' : 'h-auto'
                  )}
                />
              </div>
            )}
          </div>
          {!selectedVariant?.availableForSale && selectedVariant?.id && (
            <div className="mx-4 mt-2">
              <EtaCommunication variantId={selectedVariant?.id} />
            </div>
          )}
          {subscriptionEnabled && (
            <>
              <div className="border-grey-200 my-4 h-1 border-b border-dashed lg:block" />
              <div
                data-test="subscription-option"
                className="w-full grow px-4 sm:w-auto md:pt-0"
              >
                <OrderTypeForm
                  subscriptionDiscount={+subscriptionDiscount}
                  value={orderType}
                  onChange={changeOrderType}
                />
                <SubscriptionIntervalForm
                  couponPrices={couponPrices}
                  interval={subscriptionInterval}
                  isActive={orderType === OrderType.SUBSCRIPTION}
                  productId={Number(product.id)}
                  onChange={onSubscriptionIntervalChange}
                  subscriptionDiscount={+subscriptionDiscount}
                />
              </div>
            </>
          )}
        </div>

        {isEc &&
          product.availableForSale &&
          contentful.fields.crossSellProductHandle &&
          contentful.fields.crossSellProductVariantId &&
          crossSellPdpFlg && (
            <div className="my-4">
              <CrossSellPDP
                handle={contentful.fields.crossSellProductHandle}
                variantId={contentful.fields.crossSellProductVariantId + ''}
              />
            </div>
          )}

        {!product.availableForSale &&
          contentful.fields.recommendedAlternativeProductHandle && (
            <div className="my-4">
              <RecommendedAlternative
                productHandle={
                  contentful.fields.recommendedAlternativeProductHandle
                }
              />
            </div>
          )}
        {selectedVariantDiscount && couponPrices && (
          <CouponDisplay
            orderType={orderType}
            couponPrices={couponPrices}
            discountedPrice={selectedVariantDiscount}
            className="mb-4"
          />
        )}

        {selectedVariantId && (
          <div className="my-4 flex items-center">
            <ShippingElement
              isPreorderProduct={isPreorderProduct}
              isVariantAvailable={isVariantAvailable}
            />

            {variantTest773 !== 1 && (
              <div className="col-span-1">
                {selectedVariantDiscount && (
                  <PriceElement
                    className="mt-2"
                    couponPrices={couponPrices}
                    oneOffPrice={oneOffPrice}
                    orderType={orderType}
                    price={price}
                    pricePerKilo={pricePerKilo}
                    product={product}
                    selectedVariant={selectedVariant}
                    hasInfoPrice
                    selectedVariantDiscount={selectedVariantDiscount}
                  />
                )}
              </div>
            )}
          </div>
        )}

        <BuyButton
          className="mt-4"
          isCartAnimationOn={isCartAnimationOn}
          onAddToCart={_onAddToCart}
          onBackInStockClick={() => setBackInStockModalVisible(true)}
          product={product}
          selectedVariant={selectedVariant}
        />
      </form>

      {!isTestOosItem &&
        hasSubscription &&
        selectedVariantId &&
        (subscriptionEnabled || oneOffItemsInput) && (
          <>
            {customer && subResult && (
              <SubscriptionConfirmationInformation
                customerId={customer.id}
                subResult={subResult}
                onCloseCallback={() => {
                  setSubResult(null);
                }}
              />
            )}

            <div className="my-4">
              <AddToExistingSubscriptionButton
                disabled={!isVariantAvailable || !product.availableForSale}
                callback={callback}
                orderType={orderType}
                oneOffItemsInput={oneOffItemsInput}
                subscriptionItemsInput={
                  subscriptionEnabled && orderType === 'subscription'
                    ? [
                        {
                          quantity: variantQuantity,
                          masterProductId: Number(product.id),
                          masterVariantId: Number(selectedVariantId),
                        },
                      ]
                    : undefined
                }
              />
            </div>
          </>
        )}

      {product && selectedVariant && !selectedVariant.availableForSale && (
        <BackInStockModal
          isOpen={backInStockModalVisible}
          selectedVariant={selectedVariant}
          variants={product.variants}
          title={product.title}
          onClose={() => setBackInStockModalVisible(false)}
        />
      )}
    </>
  );
};

export default BuyPanel;
